<template>
  <loading-view-images v-if="loading" :class="{ 'tw-mb-8': !noMargin }" />
  <image-slider
    v-else
    :no-margin="noMargin"
    :items="imagesData"
    :mod-id="modId"
  />
</template>

<script>
import LoadingViewImages from '@components/Loading/LoadingViewImages.vue'
import { youtubeParser, isObjEmpty, isArray } from '@helpers/utils.js'
import ImageSlider from '@components/Slider/ImageSlider.vue'
import { PREVIEW_IMAGE_KEY, CARD_IMAGE_KEY } from '@config'
import { getSketchFabThumbsRequest } from '@services'
import { computed, toRefs, watch, ref } from 'vue'
import { useAsync } from '@composables'

export default {
  components: {
    LoadingViewImages,
    ImageSlider,
  },
  props: {
    modId: {
      type: Number,
      default: 0,
    },
    logoImage: {
      type: Object,
      default: null,
    },
    images: {
      type: Array,
      default: () => [],
    },
    youtube: {
      type: Array,
      default: () => [],
    },
    sketchfab: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    noMargin: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const {
      sketchfab: sketchfabProp,
      logoImage,
      youtube,
      images,
    } = toRefs(props)

    const sketchFabRef = ref(null)
    const sketchFab = computed(() =>
      sketchFabRef.value ? sketchFabRef.value : []
    )

    const { data, error, run } = useAsync((uuids) =>
      getSketchFabThumbsRequest(uuids)
    )

    function extractImage(element) {
      return {
        thumb: element[CARD_IMAGE_KEY],
        preview: element[PREVIEW_IMAGE_KEY],
        image: element.original,
        type: 'image',
      }
    }

    const previewData = computed(() => {
      const imagesTemp =
        images.value && isArray(images.value)
          ? images.value.map(extractImage)
          : []

      const logoTemp =
        imagesTemp.length === 0 &&
        logoImage.value &&
        !isObjEmpty(logoImage.value)
          ? [extractImage(logoImage.value)]
          : []

      return [...logoTemp, ...imagesTemp]
    })

    const imagesData = computed(() => {
      const youtubeTemp =
        youtube.value && youtube.value.length > 0 && isArray(youtube.value)
          ? youtube.value.map((element) => {
              let youtubeId = youtubeParser(element)
              return {
                link: `https://www.youtube.com/embed/${youtubeId}`,
                id: youtubeId,
                iframe: true,
                type: 'youtube',
              }
            })
          : []

      return [...previewData.value, ...youtubeTemp, ...sketchFab.value]
    })

    watch(sketchfabProp, (value) => {
      if (value.length && !sketchFabRef.value) {
        fetchSketchFabThumbs()
      }
    })

    async function fetchSketchFabThumbs() {
      const uuids = sketchfabProp.value.map((f) => _getSketchfabUuid(f))
      await run(uuids)
      if (!error.value) {
        _parseSketchFabData()
      }
    }

    function _getSketchfabUuid(url) {
      return url.split('/').pop()
    }

    function _parseSketchFabData() {
      const items = []
      sketchfabProp.value.forEach((u) => {
        const uuid = _getSketchfabUuid(u)
        const item = data.value.find((d) => d.uid === uuid)
        if (uuid && item) {
          const thumb = item.thumbnails?.images?.find(
            (img) => img.width === 256
          )
          items.push({
            link: `${u}/embed`,
            iframe: true,
            type: 'sketchfab',
            thumb: thumb?.url || item.thumbnails?.images?.pop().url,
          })
        }
      })

      sketchFabRef.value = items
    }

    return {
      imagesData,
    }
  },
}
</script>
